import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "present", l: "Present" },
  { v: "absent", l: "Absent" },
  { v: "half_day", l: "Half Day" },
  { v: "paid_leave", l: "Paid Leave" },
  { v: "sick_leave", l: "Sick Leave" },
  { v: "unpaid_leave", l: "Unpaid Leave" },
];

const emptyStaff = (branch_id) => ({
  branch_id, full_name: "", role_title: "Sales Staff", mobile: "",
  join_date: new Date().toISOString().slice(0, 10),
  monthly_salary: 0, paid_leave_balance: 0, sick_leave_balance: 0,
});

export default function Staff() {
  const { user, activeBranchId } = useAuth();
  const isSuper = user.role === "super_admin";
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStaff(activeBranchId));
  // Attendance
  const [attDate, setAttDate] = useState(new Date().toISOString().slice(0, 10));
  const [attMap, setAttMap] = useState({});  // staff_id -> status
  // Salary
  const [salaryMonth, setSalaryMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaryRows, setSalaryRows] = useState([]);
  const [dispOpen, setDispOpen] = useState(null);
  const [dispForm, setDispForm] = useState({ final_paid_amount: 0, payment_mode: "Cash", payment_date: "", notes: "", override_reason: "" });

  const loadStaff = async () => setStaff((await api.get("/staff")).data);
  const loadAttendance = async () => {
    if (!attDate) return;
    const { data } = await api.get(`/staff/attendance?start_date=${attDate}&end_date=${attDate}`);
    const map = {};
    data.forEach((r) => { map[r.staff_id] = r.status; });
    setAttMap(map);
  };
  const loadSalaryReport = async () => {
    if (!salaryMonth) return;
    const { data } = await api.get(`/staff/salary/report/${salaryMonth}`);
    setSalaryRows(data);
  };

  useEffect(() => { loadStaff(); }, []);
  useEffect(() => { loadAttendance(); /* eslint-disable-next-line */ }, [attDate, staff.length]);
  useEffect(() => { loadSalaryReport(); /* eslint-disable-next-line */ }, [salaryMonth, staff.length]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/staff/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/staff", { ...form, branch_id: form.branch_id || activeBranchId });
        toast.success("Staff added");
      }
      setOpen(false); setEditing(null); setForm(emptyStaff(activeBranchId)); loadStaff();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!isSuper) { toast.error("Only Super Admin can delete staff"); return; }
    if (!window.confirm("Remove this staff member?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); loadStaff(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (s) => { setEditing(s.id); setForm({ ...s }); setOpen(true); };

  const saveAttendance = async () => {
    const branchStaff = staff.filter((s) => !activeBranchId || s.branch_id === activeBranchId).filter((s) => s.status === "active");
    const entries = branchStaff.map((s) => ({
      staff_id: s.id, date: attDate,
      status: attMap[s.id] || "absent",
      check_in: "", check_out: "", notes: "",
    }));
    try {
      await api.post("/staff/attendance/bulk", { branch_id: activeBranchId, date: attDate, entries });
      toast.success(`Attendance saved for ${entries.length} staff`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDisperse = (row) => {
    setDispOpen(row);
    setDispForm({
      final_paid_amount: row.calculated_salary || row.monthly_salary,
      payment_mode: "Cash",
      payment_date: new Date().toISOString().slice(0, 10),
      notes: "", override_reason: "",
    });
  };

  const disperse = async () => {
    try {
      await api.post("/staff/salary/disperse", {
        staff_id: dispOpen.staff_id, month: salaryMonth,
        final_paid_amount: parseFloat(dispForm.final_paid_amount) || 0,
        payment_mode: dispForm.payment_mode, payment_date: dispForm.payment_date,
        notes: dispForm.notes, override_reason: dispForm.override_reason,
      });
      toast.success("Salary dispersed — expense auto-created");
      setDispOpen(null); loadSalaryReport();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const branchStaff = staff.filter((s) => !activeBranchId || s.branch_id === activeBranchId);

  return (
    <div className="space-y-6" data-testid="staff-page">
      <Tabs defaultValue="staff">
        <TabsList className="rounded-none">
          <TabsTrigger value="staff" className="rounded-none" data-testid="tab-staff">Staff</TabsTrigger>
          <TabsTrigger value="attendance" className="rounded-none" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="salary" className="rounded-none" data-testid="tab-salary">Salary</TabsTrigger>
        </TabsList>

        {/* Staff tab */}
        <TabsContent value="staff" className="pt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyStaff(activeBranchId)); } }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-2" />Add Staff</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-lg">
                <DialogHeader><DialogTitle>{editing ? "Edit Staff" : "New Staff Member"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Full Name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-none mt-1" data-testid="staff-name" /></div>
                  <div><Label>Role / Designation</Label><Input required value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="rounded-none mt-1" data-testid="staff-role" /></div>
                  <div><Label>Mobile</Label><Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-none mt-1" data-testid="staff-mobile" /></div>
                  <div><Label>Join Date</Label><Input required type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>Monthly Salary (₹)</Label><Input required type="number" min="0" step="0.01" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: parseFloat(e.target.value) || 0 })} className="rounded-none mt-1" data-testid="staff-salary" /></div>
                  <div><Label>Paid Leave / year</Label><Input type="number" min="0" value={form.paid_leave_balance} onChange={(e) => setForm({ ...form, paid_leave_balance: parseInt(e.target.value) || 0 })} className="rounded-none mt-1" /></div>
                  <div><Label>Sick Leave / year</Label><Input type="number" min="0" value={form.sick_leave_balance} onChange={(e) => setForm({ ...form, sick_leave_balance: parseInt(e.target.value) || 0 })} className="rounded-none mt-1" /></div>
                  <DialogFooter className="col-span-2"><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="staff-save">{editing ? "Update" : "Add"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Mobile</th><th className="px-4 py-3 text-right">Salary</th>
                <th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {branchStaff.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No staff yet.</td></tr>}
                {branchStaff.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.full_name}</td>
                    <td className="px-4 py-3 text-slate-600">{s.role_title}</td>
                    <td className="px-4 py-3 text-slate-600">{s.mobile}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatINR(s.monthly_salary)}</td>
                    <td className="px-4 py-3 text-center"><Badge className={`rounded-none ${s.status === "active" ? "bg-green-600" : "bg-slate-400"}`}>{s.status || "active"}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => startEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                      {isSuper && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-red-600" onClick={() => remove(s.id)} data-testid={`delete-staff-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Attendance tab */}
        <TabsContent value="attendance" className="pt-4 space-y-3">
          <div className="flex items-center gap-3">
            <div><Label>Date</Label><Input type="date" value={attDate} onChange={(e) => setAttDate(e.target.value)} className="rounded-none mt-1" data-testid="attendance-date" /></div>
            <Button onClick={saveAttendance} className="bg-slate-900 hover:bg-slate-800 rounded-none mt-6" data-testid="save-attendance"><CheckCircle2 className="w-4 h-4 mr-2" />Save Attendance</Button>
          </div>
          <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="px-4 py-3 text-left">Staff</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-left">Status</th>
              </tr></thead>
              <tbody>
                {branchStaff.filter((s) => s.status === "active").map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{s.full_name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{s.role_title}</td>
                    <td className="px-4 py-3">
                      <Select value={attMap[s.id] || ""} onValueChange={(v) => setAttMap({ ...attMap, [s.id]: v })}>
                        <SelectTrigger className="rounded-none h-8 w-40" data-testid={`att-status-${s.id}`}><SelectValue placeholder="Mark status" /></SelectTrigger>
                        <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Salary tab */}
        <TabsContent value="salary" className="pt-4 space-y-3">
          <div>
            <Label>Month</Label>
            <Input type="month" value={salaryMonth} onChange={(e) => setSalaryMonth(e.target.value)} className="rounded-none mt-1 max-w-xs" data-testid="salary-month" />
          </div>
          <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="px-4 py-3 text-left">Staff</th><th className="px-4 py-3 text-right">Monthly</th>
                <th className="px-4 py-3 text-center">Days</th><th className="px-4 py-3 text-right">Calculated</th>
                <th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {salaryRows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No staff active for this month.</td></tr>}
                {salaryRows.map((r) => (
                  <tr key={r.staff_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`salary-row-${r.staff_id}`}>
                    <td className="px-4 py-3 font-medium">{r.staff_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.monthly_salary)}</td>
                    <td className="px-4 py-3 text-center">{r.days_credit} / {r.working_days}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.calculated_salary)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.final_paid_amount != null ? formatINR(r.final_paid_amount) : "-"}</td>
                    <td className="px-4 py-3 text-center"><Badge className={`rounded-none ${r.status === "dispersed" ? "bg-green-600" : "bg-amber-500"}`}>{r.status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" && <Button size="sm" className="rounded-none h-7 bg-slate-900 hover:bg-slate-800" onClick={() => openDisperse(r)} data-testid={`disperse-${r.staff_id}`}>Disperse</Button>}
                      {r.status === "dispersed" && isSuper && <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => openDisperse(r)} data-testid={`redisperse-${r.staff_id}`}>Edit</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dispersement dialog */}
      <Dialog open={!!dispOpen} onOpenChange={(v) => { if (!v) setDispOpen(null); }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Disperse Salary — {dispOpen?.staff_name} ({salaryMonth})</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border border-slate-200 p-2 text-xs">
              Calculated: <b>{formatINR(dispOpen?.calculated_salary || 0)}</b> — {dispOpen?.days_credit}/{dispOpen?.working_days} days
            </div>
            <div><Label>Final Paid Amount (₹)</Label><Input type="number" step="0.01" value={dispForm.final_paid_amount} onChange={(e) => setDispForm({ ...dispForm, final_paid_amount: e.target.value })} className="rounded-none mt-1" data-testid="disp-amount" /></div>
            <div><Label>Payment Mode</Label>
              <Select value={dispForm.payment_mode} onValueChange={(v) => setDispForm({ ...dispForm, payment_mode: v })}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem><SelectItem value="UPI">UPI</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Payment Date</Label><Input type="date" value={dispForm.payment_date} onChange={(e) => setDispForm({ ...dispForm, payment_date: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Notes</Label><Input value={dispForm.notes} onChange={(e) => setDispForm({ ...dispForm, notes: e.target.value })} className="rounded-none mt-1" /></div>
            {parseFloat(dispForm.final_paid_amount) !== (dispOpen?.calculated_salary || 0) && (
              <div><Label>Override Reason (required if overriding)</Label><Input value={dispForm.override_reason} onChange={(e) => setDispForm({ ...dispForm, override_reason: e.target.value })} className="rounded-none mt-1" data-testid="disp-override" /></div>
            )}
          </div>
          <DialogFooter><Button onClick={disperse} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="disp-confirm">Disperse & Create Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
